import { useState, FormEvent } from 'react';

export default function BookingForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    deliveryDate: '',
    pickupDate: '',
    package: '',
    addons: [] as string[]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const packages = [
    'Apartment Move - 12 totes - $125',
    'Standard Home Move - 20 totes - $165',
    'Large Home Move - 32 totes - $224',
    'Estate Move - 50 totes - $300'
  ];

  const addons = [
    'Extra day rental - $20/day',
    'Extra totes - $6 each',
    '2-Wheel Hand Truck - $20',
    '4-Wheel Dolly - $10/pc',
    'Moving Blankets (8 pack) - $20',
    'Moving Blankets (12 pack) - $30',
    'Bubble Wrap - 12" x 250\' - $35/roll',
    'Label and marker kit - $10'
  ];

  const handleAddonChange = (addon: string) => {
    setFormData(prev => ({
      ...prev,
      addons: prev.addons.includes(addon)
        ? prev.addons.filter(a => a !== addon)
        : [...prev.addons, addon]
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('deliveryDate', formData.deliveryDate);
      formBody.append('pickupDate', formData.pickupDate);
      formBody.append('package', formData.package);
      formData.addons.forEach(addon => {
        formBody.append('addons', addon);
      });

      const response = await fetch('https://readdy.ai/api/form/d6orseblgirdouph31i0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          deliveryDate: '',
          pickupDate: '',
          package: '',
          addons: []
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking-form" className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Reserve Your Totes</h2>
          <p className="text-lg text-gray-600">
            Fill out the form below and we'll get back to you shortly
          </p>
        </div>

        <form id="booking-reservation-form" data-readdy-form onSubmit={handleSubmit} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="address" className="block text-sm font-semibold text-gray-900 mb-2">Moving Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="deliveryDate" className="block text-sm font-semibold text-gray-900 mb-2">Delivery Date</label>
              <input
                type="date"
                id="deliveryDate"
                name="deliveryDate"
                value={formData.deliveryDate}
                onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              />
            </div>
            <div>
              <label htmlFor="pickupDate" className="block text-sm font-semibold text-gray-900 mb-2">Pickup Date</label>
              <input
                type="date"
                id="pickupDate"
                name="pickupDate"
                value={formData.pickupDate}
                onChange={(e) => setFormData({ ...formData, pickupDate: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="package" className="block text-sm font-semibold text-gray-900 mb-2">Package Selection</label>
            <select
              id="package"
              name="package"
              value={formData.package}
              onChange={(e) => setFormData({ ...formData, package: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select a package</option>
              {packages.map((pkg, index) => (
                <option key={index} value={pkg}>{pkg}</option>
              ))}
            </select>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Add-Ons (Optional)</label>
            <div className="space-y-3">
              {addons.map((addon, index) => (
                <label key={index} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="addons"
                    value={addon}
                    checked={formData.addons.includes(addon)}
                    onChange={() => handleAddonChange(addon)}
                    className="w-5 h-5 rounded border-gray-300 text-yellow-400 focus:ring-yellow-400 cursor-pointer"
                  />
                  <span className="text-gray-700">{addon}</span>
                </label>
              ))}
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium">Thank you! Your reservation has been submitted. A member of our team will reach out to you within 1 business day to set up your move!</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 font-medium">Something went wrong. Please try again.</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting...' : 'Reserve My Moving Totes'}
          </button>
        </form>
      </div>
    </section>
  );
}
